import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';

const LOGO = 'https://public.readdy.ai/ai/img_res/1641229f-de9d-42a9-901f-6e578c489111.png';

const NAV_LINKS = [
  { label: 'КАТАЛОГ', to: '/catalog', icon: 'ri-medicine-bottle-line' },
  { label: 'АКЦИИ', to: '/promos', icon: 'ri-price-tag-3-line' },
  { label: 'БОНУСЫ', to: '/loyalty', icon: 'ri-vip-crown-line' },
  { label: 'АПТЕКИ', to: '/pharmacies', icon: 'ri-store-2-line' },
  { label: 'ДОСТАВКА', to: '/delivery', icon: 'ri-truck-line' },
  { label: 'БЛОГ', to: '/blog', icon: 'ri-article-line' },
  { label: 'О НАС', to: '/about', icon: 'ri-information-line' },
  { label: 'КОНТАКТЫ', to: '/contacts', icon: 'ri-phone-line' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { totalCount, openCart } = useCart();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'backdrop-blur-md border-b border-white/8' : ''
      }`}
      style={{
        background: scrolled
          ? 'rgba(5,5,8,0.95)'
          : 'linear-gradient(to bottom, rgba(5,5,8,0.85) 0%, rgba(5,5,8,0) 100%)',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
      }}
    >
      <div className="px-6 lg:px-12 h-18 flex items-center justify-between gap-6" style={{ height: '72px' }}>

        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer shrink-0">
          <div className="w-10 h-10 flex items-center justify-center relative">
            <img src={LOGO} alt="NeonPharma" className="w-full h-full object-contain" />
            <div className="absolute inset-0 rounded-full opacity-40 animate-neon-pulse" style={{ boxShadow: '0 0 12px #ff2d78' }} />
          </div>
          <div className="hidden sm:block">
            <div className="font-orbitron font-bold text-base text-white" style={{ textShadow: '0 0 10px rgba(255,45,120,0.4)' }}>
              NEON<span style={{ color: '#00f5ff', textShadow: '0 0 10px rgba(0,245,255,0.4)' }}>PHARMA</span>
            </div>
            <div className="font-mono text-[10px] tracking-[0.2em]" style={{ color: 'rgba(0,245,255,0.5)' }}>ИНТЕРНЕТ-АПТЕКА</div>
          </div>
        </Link>

        {/* Desktop nav links — center */}
        <div className="hidden lg:flex items-center gap-1 flex-1 justify-center">
          {NAV_LINKS.map(link => {
            const isActive = location.pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                className="relative px-3 py-2 rounded-lg font-mono text-xs tracking-wider transition-all duration-200 whitespace-nowrap"
                style={{
                  color: isActive ? '#ff2d78' : 'rgba(255,255,255,0.55)',
                  background: isActive ? 'rgba(255,45,120,0.08)' : 'transparent',
                }}
                onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.9)'; }}
                onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.55)'; }}
              >
                {link.label}
                {isActive && (
                  <span className="absolute bottom-0 left-3 right-3 h-px rounded-full" style={{ background: '#ff2d78', boxShadow: '0 0 6px #ff2d78' }} />
                )}
              </Link>
            );
          })}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Search */}
          <button
            onClick={() => navigate('/search')}
            title="Поиск"
            className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-all duration-200"
            style={{ color: 'rgba(255,255,255,0.5)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#00f5ff'; (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.08)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            <i className="ri-search-line text-lg" />
          </button>

          {/* Profile */}
          <Link
            to="/profile"
            title="Личный кабинет"
            className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-all duration-200"
            style={{
              color: location.pathname === '/profile' ? '#f0ff00' : 'rgba(255,255,255,0.5)',
              background: location.pathname === '/profile' ? 'rgba(240,255,0,0.08)' : 'transparent',
            }}
            onMouseEnter={e => { if (location.pathname !== '/profile') { (e.currentTarget as HTMLElement).style.color = '#f0ff00'; (e.currentTarget as HTMLElement).style.background = 'rgba(240,255,0,0.08)'; } }}
            onMouseLeave={e => { if (location.pathname !== '/profile') { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; (e.currentTarget as HTMLElement).style.background = 'transparent'; } }}
          >
            <i className="ri-user-line text-lg" />
          </Link>

          {/* Cart */}
          <button
            onClick={openCart}
            title="Корзина"
            className="relative w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-all duration-200"
            style={{ color: 'rgba(255,255,255,0.5)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#ff2d78'; (e.currentTarget as HTMLElement).style.background = 'rgba(255,45,120,0.08)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            <i className="ri-shopping-bag-line text-lg" />
            {totalCount > 0 && (
              <span
                className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full font-orbitron font-black text-[9px]"
                style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 8px rgba(255,45,120,0.7)' }}
              >
                {totalCount > 9 ? '9+' : totalCount}
              </span>
            )}
          </button>

          {/* CTA — Catalog */}
          <Link
            to="/catalog"
            className="hidden md:flex whitespace-nowrap items-center gap-1.5 px-4 py-2 rounded-full font-orbitron font-bold text-xs tracking-wider cursor-pointer transition-all duration-300"
            style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 15px rgba(255,45,120,0.4)' }}
          >
            <i className="ri-medicine-bottle-line" />
            КАТАЛОГ
          </Link>

          {/* Hamburger */}
          <button
            onClick={() => setMenuOpen(v => !v)}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors"
            style={{ color: '#00f5ff' }}
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-3-line text-xl'} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          className="lg:hidden border-t"
          style={{ background: 'rgba(5,5,8,0.98)', backdropFilter: 'blur(20px)', borderColor: 'rgba(255,45,120,0.15)' }}
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map(link => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl font-mono text-sm transition-all duration-200"
                  style={{
                    color: isActive ? '#ff2d78' : 'rgba(255,255,255,0.7)',
                    background: isActive ? 'rgba(255,45,120,0.08)' : 'transparent',
                    borderLeft: isActive ? '3px solid #ff2d78' : '3px solid transparent',
                  }}
                >
                  <i className={link.icon} style={{ color: isActive ? '#ff2d78' : 'rgba(255,255,255,0.3)', fontSize: '16px' }} />
                  {link.label}
                </Link>
              );
            })}
            <div className="pt-3 mt-2 border-t flex gap-2" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <Link to="/search" className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-mono text-sm cursor-pointer" style={{ border: '1px solid rgba(0,245,255,0.25)', color: '#00f5ff' }}>
                <i className="ri-search-line" />
                Поиск
              </Link>
              <Link to="/profile" className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-mono text-sm cursor-pointer" style={{ border: '1px solid rgba(240,255,0,0.25)', color: '#f0ff00' }}>
                <i className="ri-user-line" />
                Кабинет
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Active page indicator line */}
      {scrolled && (
        <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, #ff2d78 30%, #00f5ff 70%, transparent)' }} />
      )}
    </nav>
  );
};

export default Navbar;
